/**
 * BulkScreeningPanel — Motor de Triagem IA em massa.
 * Empresa faz upload de até 100 CVs em PDF, escolhe uma vaga OU insere descritivo livre,
 * e a IA devolve tabela ordenada por match score.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Sparkles, Upload, FileText, ChevronDown, ChevronUp, X, Wallet, ShieldCheck, Rocket, ArrowUpDown, AlertTriangle,
  CheckCircle2, Clock, History,
} from "lucide-react";

const AI_PACKS_ORDER = ["ai_screening_50", "ai_screening_100"];

function ScoreBadge({ score }) {
  if (score === null || score === undefined)
    return <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><AlertTriangle className="w-3.5 h-3.5" /> —</span>;
  const color = score >= 75 ? "bg-emerald-100 text-emerald-800 ring-emerald-300"
    : score >= 55 ? "bg-sky-100 text-sky-800 ring-sky-300"
    : score >= 35 ? "bg-amber-100 text-amber-800 ring-amber-300"
    : "bg-rose-100 text-rose-800 ring-rose-300";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-black ring-1 ${color}`}>
      {score}<span className="text-[9px] font-bold opacity-70">/100</span>
    </span>
  );
}

function ResultRow({ r, index, open, onToggle }) {
  return (
    <>
      <tr data-testid={`screening-row-${index}`}
          className={`border-b border-slate-100 ${r.error ? "bg-rose-50/40" : "hover:bg-slate-50/60"} cursor-pointer transition-colors`}
          onClick={onToggle}>
        <td className="py-3 px-3 text-sm font-bold text-slate-700">{index + 1}</td>
        <td className="py-3 px-3">
          <p className="font-heading font-bold text-slate-900 text-sm truncate max-w-[220px]">
            {r.candidate_name || <span className="text-slate-400 italic">Nome não detectado</span>}
          </p>
          <p className="text-[11px] text-slate-500 flex items-center gap-1 truncate max-w-[220px]">
            <FileText className="w-3 h-3" /> {r.filename}
          </p>
        </td>
        <td className="py-3 px-3"><ScoreBadge score={r.match_score} /></td>
        <td className="py-3 px-3 text-sm text-slate-600">
          {r.years_experience !== null && r.years_experience !== undefined
            ? <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {r.years_experience} anos</span>
            : <span className="text-slate-300">—</span>}
        </td>
        <td className="py-3 px-3 text-sm text-slate-600 max-w-[280px]">
          {r.error ? (
            <span className="text-rose-700 text-xs font-semibold">{r.error}</span>
          ) : (
            <span className="line-clamp-2">{r.summary || "—"}</span>
          )}
        </td>
        <td className="py-3 px-3 text-right">
          {open ? <ChevronUp className="w-4 h-4 text-slate-400 inline-block" /> : <ChevronDown className="w-4 h-4 text-slate-400 inline-block" />}
        </td>
      </tr>
      {open && !r.error && (
        <tr className="bg-slate-50/60">
          <td colSpan={6} className="py-4 px-4">
            <div className="grid sm:grid-cols-3 gap-4 text-xs">
              <div>
                <p className="uppercase tracking-[0.15em] font-black text-emerald-700 mb-2">Forças</p>
                {(r.strengths || []).length ? (
                  <ul className="space-y-1">
                    {r.strengths.map((s, i) => <li key={`str-${i}-${s.slice(0, 30)}`} className="flex gap-1.5 text-slate-700"><CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" /> {s}</li>)}
                  </ul>
                ) : <p className="text-slate-400 italic">—</p>}
              </div>
              <div>
                <p className="uppercase tracking-[0.15em] font-black text-rose-700 mb-2">Lacunas</p>
                {(r.gaps || []).length ? (
                  <ul className="space-y-1">
                    {r.gaps.map((s, i) => <li key={`gap-${i}-${s.slice(0, 30)}`} className="flex gap-1.5 text-slate-700"><AlertTriangle className="w-3 h-3 text-rose-500 mt-0.5 shrink-0" /> {s}</li>)}
                  </ul>
                ) : <p className="text-slate-400 italic">—</p>}
              </div>
              <div>
                <p className="uppercase tracking-[0.15em] font-black text-sky-700 mb-2">Skills identificadas</p>
                {(r.key_skills || []).length ? (
                  <div className="flex flex-wrap gap-1">
                    {r.key_skills.map((s, i) => <Badge key={`skill-${i}-${s}`} variant="secondary" className="bg-sky-100 text-sky-800 rounded-full text-[10px] font-bold">{s}</Badge>)}
                  </div>
                ) : <p className="text-slate-400 italic">—</p>}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function BulkScreeningPanel({ jobs = [] }) {
  const [wallet, setWallet] = useState({ credits: 0, packs: [] });
  const [jobId, setJobId] = useState("");
  const [description, setDescription] = useState("");
  const [contextName, setContextName] = useState("");
  const [files, setFiles] = useState([]);
  const [running, setRunning] = useState(false);
  const [buying, setBuying] = useState(null);
  const [results, setResults] = useState(null);
  const [openRow, setOpenRow] = useState(null);
  const [sortBy, setSortBy] = useState("score_desc");
  const [history, setHistory] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);

  const reloadWallet = useCallback(() => {
    api.get("/company/screening/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/company/screening").then((r) => setHistory(r.data.screenings || [])).catch(() => {});
  }, []);

  useEffect(() => { reloadWallet(); }, [reloadWallet]);

  const activeJobs = (jobs || []).filter(j => j.status === "active");

  const useDescription = jobId === "__desc__" || activeJobs.length === 0;

  const onPickFiles = (evt) => {
    const list = Array.from(evt.target.files || []);
    const pdfs = list.filter(f => (f.type === "application/pdf") || (f.name || "").toLowerCase().endsWith(".pdf"));
    if (pdfs.length !== list.length) toast.warning(`${list.length - pdfs.length} ficheiro(s) ignorados — só PDF.`);
    if (pdfs.length > 100) {
      toast.warning("Máximo 100 CVs por triagem.");
      setFiles(pdfs.slice(0, 100));
    } else {
      setFiles(pdfs);
    }
    evt.target.value = "";
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const buyPack = async (pack) => {
    setBuying(pack);
    try {
      const { data } = await api.post("/payments/checkout-pack", { pack, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatErr(e));
      setBuying(null);
    }
  };

  const runScreening = async () => {
    if (files.length === 0) return toast.warning("Adiciona pelo menos 1 CV em PDF.");
    if (!useDescription && !jobId) return toast.warning("Escolhe uma vaga ou o descritivo livre.");
    if (useDescription && description.trim().length < 20) return toast.warning("O descritivo tem de ter pelo menos 20 caracteres.");
    if (wallet.credits < files.length) return toast.error(`Sem créditos suficientes: tens ${wallet.credits}, precisas de ${files.length}.`);

    setRunning(true);
    setResults(null);
    setSelectedHistory(null);
    try {
      const fd = new FormData();
      if (useDescription) {
        fd.append("description", description.trim());
        if (contextName.trim()) fd.append("name", contextName.trim());
      } else {
        fd.append("job_id", jobId);
      }
      files.forEach(f => fd.append("files", f));
      const { data } = await api.post("/company/screening", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResults(data);
      toast.success(`Triagem concluída — ${data.results.length} CVs analisados. ${data.credits_used} créditos usados.`);
      reloadWallet();
      setFiles([]);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setRunning(false);
    }
  };

  const loadHistoryItem = async (screeningId) => {
    try {
      const { data } = await api.get(`/company/screening/${screeningId}`);
      const s = data.screening;
      setResults({ screening_id: s.id, results: s.results, context_label: s.context_label, job_title: s.job_title, credits_used: s.credits_used, created_at: s.created_at });
      setSelectedHistory(s.id);
      setOpenRow(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const sortedResults = (() => {
    if (!results?.results) return [];
    const arr = [...results.results];
    if (sortBy === "score_desc") arr.sort((a, b) => (b.match_score ?? -1) - (a.match_score ?? -1));
    if (sortBy === "score_asc") arr.sort((a, b) => (a.match_score ?? 999) - (b.match_score ?? 999));
    if (sortBy === "name") arr.sort((a, b) => (a.candidate_name || a.filename).localeCompare(b.candidate_name || b.filename));
    if (sortBy === "experience") arr.sort((a, b) => (b.years_experience ?? -1) - (a.years_experience ?? -1));
    return arr;
  })();

  return (
    <div className="space-y-8">
      {/* Wallet + packs de créditos */}
      <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 rounded-2xl p-6 sm:p-8 text-white border border-white/10 shadow-2xl">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <Sparkles className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-fuchsia-300">Motor de Triagem IA</p>
              <h2 className="font-heading font-black tracking-tighter text-2xl sm:text-3xl">
                Ordena 100 CVs em segundos
              </h2>
              <p className="text-sm text-white/70 mt-1 max-w-xl">
                Faz upload dos CVs e a IA ordena-os por match score contra a tua vaga ou descritivo. 1 crédito = 1 CV analisado.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-5 py-4 backdrop-blur">
            <Wallet className="w-6 h-6 text-fuchsia-300" strokeWidth={2} />
            <div>
              <p data-testid="ai-screening-credits" className="font-heading font-black tracking-tighter text-3xl">{wallet.credits}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60">Créditos disponíveis</p>
            </div>
          </div>
        </div>

        {/* Packs */}
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {AI_PACKS_ORDER.map((packId) => {
            const p = (wallet.packs || []).find(x => x.pack === packId);
            if (!p) return null;
            const pricePer = ((p.amount / 100) / p.credits).toFixed(2);
            const isBig = packId === "ai_screening_100";
            return (
              <div key={p.pack}
                   data-testid={`ai-pack-${p.pack}`}
                   className={`relative rounded-2xl p-5 border-2 ${isBig ? "border-amber-400 bg-gradient-to-br from-amber-500/20 via-white/5 to-yellow-500/10" : "border-white/20 bg-white/5"}`}>
                {isBig && (
                  <span className="absolute -top-3 left-4 bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest">
                    ★ Melhor preço
                  </span>
                )}
                <div className="flex items-center gap-2 mb-2">
                  {isBig ? <Rocket className="w-5 h-5 text-amber-300" /> : <ShieldCheck className="w-5 h-5 text-white/70" />}
                  <p className="font-heading font-black tracking-tighter text-lg">{p.label}</p>
                </div>
                <p className="text-sm text-white/70 mb-4">{p.description || p.tagline}</p>
                <div className="flex items-end justify-between mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className={`font-heading font-black tracking-tighter text-4xl ${isBig ? "text-amber-300" : "text-white"}`}>
                      {(p.amount / 100).toFixed(0)}
                    </span>
                    <span className="text-xl font-black">€</span>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black">{p.credits}</p>
                    <p className="text-[10px] uppercase tracking-widest text-white/50">análises</p>
                    <p className="text-[10px] text-fuchsia-300 mt-0.5">{pricePer}€ / CV</p>
                  </div>
                </div>
                <Button data-testid={`buy-ai-pack-${p.pack}`}
                        onClick={() => buyPack(p.pack)} disabled={buying === p.pack}
                        className={`w-full rounded-full font-bold ${isBig ? "bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-slate-950 shadow-lg" : "bg-white text-slate-900 hover:bg-white/90"}`}>
                  {buying === p.pack ? "A redirecionar…" : `Comprar pack ${(p.amount / 100).toFixed(0)}€`}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Formulário de triagem */}
      <div className="bg-white rounded-2xl border border-black/5 p-6 sm:p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-fuchsia-100 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-fuchsia-600" />
          </div>
          <div>
            <h3 className="font-heading font-black tracking-tighter text-xl text-slate-900">Nova triagem</h3>
            <p className="text-xs text-slate-500">Escolhe uma vaga (recomendado) ou insere um descritivo livre. Depois carrega os CVs.</p>
          </div>
        </div>

        {/* Contexto: vaga OU descritivo */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Vaga (opcional)</Label>
            <Select value={jobId} onValueChange={(v) => { setJobId(v); }}>
              <SelectTrigger data-testid="screening-job-select" className="mt-1 rounded-xl">
                <SelectValue placeholder={activeJobs.length === 0 ? "Sem vagas ativas — usa descritivo" : "Escolhe uma vaga ativa"} />
              </SelectTrigger>
              <SelectContent>
                {activeJobs.map(j => (
                  <SelectItem key={j.id} value={j.id}>{j.title} — {j.concelho}, {j.district}</SelectItem>
                ))}
                <SelectItem value="__desc__">📝 Usar descritivo livre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {useDescription && (
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Etiqueta (opcional)</Label>
              <Input data-testid="screening-context-name" className="mt-1 rounded-xl" placeholder="Ex.: Cozinheiro Chiado"
                     value={contextName} onChange={(e) => setContextName(e.target.value)} maxLength={80} />
            </div>
          )}
        </div>

        {useDescription && (
          <div className="mt-4">
            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Descritivo da vaga</Label>
            <Textarea data-testid="screening-description"
                      className="mt-1 rounded-xl min-h-[110px]"
                      placeholder="Ex.: Procuramos Cozinheiro/a com experiência em cozinha portuguesa, HACCP, capacidade de liderar equipa de 5-10 pessoas. Full-time em Lisboa. Contrato de trabalho e refeição incluída."
                      value={description} onChange={(e) => setDescription(e.target.value)} maxLength={3000} />
            <p className="text-[11px] text-slate-400 mt-1">{description.length}/3000 · mínimo 20 caracteres</p>
          </div>
        )}

        {/* Upload de CVs */}
        <div className="mt-6">
          <Label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">CVs em PDF (até 100)</Label>
          <label htmlFor="screening-files"
                 className="mt-2 block cursor-pointer rounded-2xl border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/40 hover:bg-fuchsia-50 hover:border-fuchsia-400 transition-colors p-6 text-center">
            <Upload className="w-6 h-6 text-fuchsia-500 mx-auto mb-2" />
            <p className="text-sm font-bold text-slate-900">Clica para escolher CVs em PDF</p>
            <p className="text-xs text-slate-500 mt-0.5">Até 100 ficheiros · máx. 8MB cada</p>
            <input id="screening-files" data-testid="screening-file-input" type="file" accept="application/pdf" multiple onChange={onPickFiles} className="hidden" />
          </label>

          {files.length > 0 && (
            <div className="mt-4 space-y-2 max-h-64 overflow-auto pr-1">
              <div className="flex justify-between items-center">
                <p className="text-xs font-bold text-slate-700">
                  {files.length} CV{files.length > 1 ? "s" : ""} pronto{files.length > 1 ? "s" : ""} · custa <b className="text-fuchsia-700">{files.length} crédito{files.length > 1 ? "s" : ""}</b>
                </p>
                <button onClick={() => setFiles([])} className="text-xs text-slate-500 hover:text-rose-600">Limpar tudo</button>
              </div>
              {files.map((f, i) => (
                <div key={`file-${i}-${f.name}-${f.size}`} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2">
                  <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
                  <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-rose-600 shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {wallet.credits < files.length
              ? <span className="text-rose-600 font-bold">Faltam {files.length - wallet.credits} créditos — compra um pack.</span>
              : <>Vais gastar <b>{files.length} de {wallet.credits}</b> créditos disponíveis.</>}
          </p>
          <Button data-testid="screening-run-btn"
                  onClick={runScreening}
                  disabled={running || files.length === 0 || wallet.credits < files.length}
                  className="rounded-full bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-700 hover:to-indigo-700 text-white shadow-lg font-bold px-6 py-6">
            {running ? "IA a analisar…" : <><Sparkles className="w-4 h-4 mr-2" /> Analisar {files.length} CV{files.length > 1 ? "s" : ""} com IA</>}
          </Button>
        </div>
      </div>

      {/* Resultados */}
      {results && (
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-fuchsia-600">Resultado da triagem IA</p>
              <h3 className="font-heading font-black tracking-tighter text-xl text-slate-900">
                {results.job_title || results.context_label || "Triagem"}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {results.results.length} candidatos analisados · {results.credits_used} créditos usados
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Ordenar por</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="rounded-full w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="score_desc">↓ Match score (top primeiro)</SelectItem>
                  <SelectItem value="score_asc">↑ Match score (menos primeiro)</SelectItem>
                  <SelectItem value="experience">↓ Anos de experiência</SelectItem>
                  <SelectItem value="name">A-Z Nome</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr className="text-[10px] uppercase tracking-[0.15em] font-black text-slate-500">
                  <th className="text-left py-3 px-3">#</th>
                  <th className="text-left py-3 px-3">Candidato</th>
                  <th className="text-left py-3 px-3"><span className="inline-flex items-center gap-1"><ArrowUpDown className="w-3 h-3" /> Match</span></th>
                  <th className="text-left py-3 px-3">Experiência</th>
                  <th className="text-left py-3 px-3">Resumo</th>
                  <th className="text-right py-3 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {sortedResults.map((r, i) => (
                  <ResultRow key={r.index}
                             r={r}
                             index={i}
                             open={openRow === r.index}
                             onToggle={() => setOpenRow(openRow === r.index ? null : r.index)} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Histórico */}
      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-slate-600" />
            <h3 className="font-heading font-black tracking-tighter text-lg text-slate-900">Histórico de triagens</h3>
          </div>
          <div className="space-y-2">
            {history.map(s => (
              <button key={s.id}
                      data-testid={`history-item-${s.id}`}
                      onClick={() => loadHistoryItem(s.id)}
                      className={`w-full text-left rounded-xl px-4 py-3 border transition-colors ${selectedHistory === s.id ? "border-fuchsia-400 bg-fuchsia-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 text-sm truncate">{s.context_label || s.job_title || "Triagem sem título"}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {new Date(s.created_at).toLocaleString("pt-PT")} · {s.cv_count} CVs · {s.credits_used} créditos
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
